import ApolloClient from 'apollo-client'
import * as Sentry from '@sentry/browser'
import 'navigator.locks'

import { castError } from '@sketch/utils'

import {
  getUserAuthorization,
  replaceAuthorization,
} from 'graphql/multisession'
import {
  GetInitialUserDocument,
  GetInitialUserQuery,
  RefreshCredentialsDocument,
  RefreshCredentialsMutation,
  RefreshCredentialsMutationVariables,
} from '@sketch/gql-types'

const isCrossTabsRefreshFeatureFlagOn = (
  client: ApolloClient<object>
): boolean => {
  if (!client) return false

  try {
    const user = client.readQuery<GetInitialUserQuery>({
      query: GetInitialUserDocument,
    })

    if (!user) return false

    return user.me.featureFlags?.includes('cross-tabs-token-refresh') || false
  } catch (err) {
    return false
  }
}

export const checkIfTokenIsExpired = (expirationDateTime: string) =>
  // Compare to the current date + 5 min. So we can preemptialy refresh
  // the token 5 min before the actual expiration time.
  // 1000ms * 60 * 5 = 5min
  new Date(expirationDateTime) < new Date(Date.now() + 1000 * 60 * 5)

const fireRefreshAccessTokenMutation = async (
  client: ApolloClient<object>,
  authToken: string,
  refreshToken: string
) => {
  const { data, errors } = await client.mutate<
    RefreshCredentialsMutation,
    RefreshCredentialsMutationVariables
  >({
    mutation: RefreshCredentialsDocument,
    variables: { authToken, refreshToken },
    update: (cache, { data }) => {
      const { credentials } = data?.refreshCredentials || {}

      if (credentials) {
        replaceAuthorization(cache, credentials, authToken)
      }
    },
  })

  const { credentials } = data?.refreshCredentials || {}

  if (errors || !credentials) {
    throw new Error('It was not possible to refresh the user credentials')
  }
}

let refreshPromise: Promise<void> | null

export const refreshToken = async function startRefreshToken(
  client: ApolloClient<object>
) {
  const credentials = getUserAuthorization(client.cache)

  try {
    if (isCrossTabsRefreshFeatureFlagOn(client)) {
      // Use a WebLock to prevent multiple tabs to refresh the token
      // at the same time and end up in a logout due to an already
      // used refresh token
      await navigator.locks.request('refresh_token', async () => {
        const actualCredentials = getUserAuthorization(client.cache)

        // The WebLock API creates a queue with all requests, so if the authToken
        // changed, it means that another call already refreshed the token before,
        // so there's no need to do this again
        if (actualCredentials?.authToken !== credentials?.authToken) return

        await fireRefreshAccessTokenMutation(
          client,
          credentials!.authToken,
          credentials!.refreshToken
        )
      })

      return true
    }

    // Check if there isn't already a token refreshing, if there isn't create a request dependency
    // so the next requests can relay on
    if (!refreshPromise) {
      refreshPromise = fireRefreshAccessTokenMutation(
        client,
        credentials!.authToken,
        credentials!.refreshToken
      )
    }

    await refreshPromise
    Sentry.addBreadcrumb({
      category: 'Authentication',
      message: `Token refreshed`,
      level: 'log',
    })
    return true
  } catch (e) {
    const error = castError(e)
    Sentry.addBreadcrumb({
      category: 'Authentication',
      message: `Refresh token request failed with error: ${error.message}`,
      level: 'error',
    })
    return false
  } finally {
    // eslint-disable-next-line require-atomic-updates
    refreshPromise = null
  }
}
